import { ChangeDetectionStrategy, Component, inject, Input, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AnnexService } from 'src/app/data/annex.service';

@Component({
  selector: 'app-annex-file-panel',
  templateUrl: './annex-file-panel.component.html',
  styleUrl: './annex-file-panel.component.scss',
  standalone: false,
})
export class AnnexFilePanelComponent {
  @Input() annexId: number;
  private annexService = inject(AnnexService);

  pdfSrc: string = null;

  private readonly minZoom = 0.5;
  private readonly maxZoom = 2.0;
  private readonly zoomStep = 0.1;
  zoom = 1;

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  get canZoomIn(): boolean {
    return this.zoom < this.maxZoom;
  }

  get canZoomOut(): boolean {
    return this.zoom > this.minZoom;
  }

  zoomIn(): void {
    this.zoom = Math.min(this.maxZoom, Math.round((this.zoom + this.zoomStep) * 100) / 100);
  }

  zoomOut(): void {
    this.zoom = Math.max(this.minZoom, Math.round((this.zoom - this.zoomStep) * 100) / 100);
  }

  zoomReset(): void {
    this.zoom = 1;
  }

  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private scrollStartLeft = 0;
  private scrollStartTop = 0;

  onPanStart(event: MouseEvent): void {
    if (event.button !== 0) { return; }
    const target = event.currentTarget as HTMLElement;
    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.scrollStartLeft = target.scrollLeft;
    this.scrollStartTop = target.scrollTop;
    target.classList.add('is-panning');
    event.preventDefault();
  }

  onPanMove(event: MouseEvent): void {
    if (!this.isPanning) { return; }
    const target = event.currentTarget as HTMLElement;
    target.scrollLeft = this.scrollStartLeft - (event.clientX - this.panStartX);
    target.scrollTop = this.scrollStartTop - (event.clientY - this.panStartY);
  }

  onPanEnd(event: MouseEvent): void {
    if (!this.isPanning) { return; }
    this.isPanning = false;
    (event.currentTarget as HTMLElement).classList.remove('is-panning');
  }

  private fileSub: Subscription;

  constructor() {}

   ngOnInit(): void {
    this.loadPdf();
  }

    ngOnChanges(changes: SimpleChanges): void {
    if (changes['annexId'] && !changes['annexId'].firstChange) {
      this.loadPdf();
    }
  }

  ngOnDestroy(): void {
    this.fileSub?.unsubscribe();
    if (this.pdfSrc) {
      URL.revokeObjectURL(this.pdfSrc);
    }
  }

  private loadPdf(): void {
    if (!this.annexId) return;
    this.fileSub?.unsubscribe();
    this.fileSub = this.annexService.showAnnexDocument(this.annexId).subscribe({
      next: (response) => {
        const blob = response.body;
        this.loadPdfFromBlob(blob);
      },
      error: (err) => {
        console.error('Error loading annex document:', err);
      }
    });
  }

  private loadPdfFromBlob(blob: Blob): void {
    if (this.pdfSrc) {
      URL.revokeObjectURL(this.pdfSrc);
    }
    this.pdfSrc = URL.createObjectURL(blob);
  }
}
